import { Injectable } from '@nestjs/common';
import { ActivityEntity } from './activity.entity';
import { ErrorResponse } from '@gtpl/shared-models/common-models';
import { InjectRepository } from '@nestjs/typeorm';
import { Raw, Repository } from 'typeorm';
import { ActivityDTO, ActivityStatusDTO } from './dto/activity.dto';
import { ActivityResponseModel } from './dto/activity-response.module';
import { AllActivityResponseModel } from './dto/all-activity-response.module';
import { RoleActivityService } from '../role-activity/role-activity.service';

@Injectable()
export class ActivityService {
    constructor(
        @InjectRepository(ActivityEntity)
        private activityRepository: Repository<ActivityEntity>,
        private roleActivityService: RoleActivityService,
    ) {}

    async createActivity(queryParams, activityDto:ActivityDTO, request: Request): Promise<ActivityResponseModel> {
        const response = new ActivityResponseModel();
        try {
            const ActivityExists = await this.getActivityWithoutRelations(activityDto.activityName);
            if(ActivityExists) {
                throw new ErrorResponse(11104, 'Activity name already exists');
            } else {
                if(queryParams.apiVersion == 1) {
                    activityDto.versionFlag = queryParams.apiVersion;
                    const savedActivity = await this.activityRepository.save(activityDto);
                    if(savedActivity) {
                        if(activityDto.roleIds.length > 0) {
                            var dataToPost =activityDto.roleIds.map((el) => {
                                return {
                                    roleId:parseInt(el),
                                    activityId: savedActivity.Id,
                                    createdUser:activityDto.createdUser
                                };
                            });                            
                            const savedRoleActivity = await this.roleActivityService.assignRoleActivity(dataToPost);
                            response.status = true;
                            response.data   = savedActivity;
                            response.statusCode = 50000;
                            response.internalMessage = 'Activity name created and assigned to roles successfully';
                            response.intlCode = 11100;
                        } else {
                            response.status = true;
                            response.data   = savedActivity;
                            response.statusCode = 50000;
                            response.internalMessage = 'Activity name created but not assigned to roles';
                            response.intlCode = 11100;
                        }                        
                    } else { 
                        throw new ErrorResponse(11106, 'Activity name not created')
                    }
                } else {
                    const savedActivity = await this.activityRepository.save(activityDto);
                    if(savedActivity) {
                        if(activityDto.roleIds.length > 0) {
                            var dataToPost =activityDto.roleIds.map((el) => {
                                return {
                                    roleId:parseInt(el),
                                    activityId: savedActivity.Id,
                                    createdUser:activityDto.createdUser
                                };
                            });                            
                            const savedRoleActivity = await this.roleActivityService.assignRoleActivity(dataToPost);
                            response.status = true;
                            response.data   = savedActivity;
                            response.statusCode = 50000;
                            response.internalMessage = 'Activity name created and assigned to roles successfully';
                            response.intlCode = 11100;
                        } else {
                            response.status = true;
                            response.data   = savedActivity;
                            response.statusCode = 50000;
                            response.internalMessage = 'Activity name created but not assigned to roles';
                            response.intlCode = 11100;
                        }                        
                    } else { 
                        throw new ErrorResponse(11106, 'Activity name not created')
                    }
                }
            }
            return response;
        } catch (error) {
            throw error;
        }
    }

    async getActivityWithoutRelations(activityName: string): Promise<ActivityEntity> {
        const activityResponse = await this.activityRepository.findOne({
            where: {activityName: Raw(alias => `UPPER(activity_name) = '${activityName.toLocaleUpperCase()}'`), status: true},
        });
        if(activityResponse) {
            return activityResponse;
        } else {
            return null;
        }
    }

    async getActiveActivities(queryParams, request:Request): Promise<AllActivityResponseModel> {
        const response = new AllActivityResponseModel();
        try {
            if(queryParams.apiVersion == 1) {
                const activeTypes = await this.activityRepository.find({
                    where: {status: true},
                    order: { createdAt: 'DESC' }
                });
                if(activeTypes) {
                    response.status = true;
                    response.data = activeTypes;
                    response.statusCode = 50000;
                    response.internalMessage = 'Data retrieved successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Data not found');
                }
            } else {
                const activeTypes = await this.activityRepository.find({
                    where: {status: true},
                    order: { createdAt: 'DESC' }
                });
                if(activeTypes) {
                    response.status = true;
                    response.data = activeTypes;
                    response.statusCode = 50000;
                    response.internalMessage = 'Data retrieved successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Data not found');
                }
            }
            return response;
        } catch (error) {
            throw error;
        }
    }

    async updateActivity(queryParams, activityDto: ActivityDTO, request:Request): Promise<ActivityResponseModel> {
        const response = new ActivityResponseModel();
        try {
            if(queryParams.apiVersion == 1) {
                const updateActivity = await this.activityRepository.update(activityDto.Id, {
                    activityName:activityDto.activityName,
                    activityUrl:activityDto.activityUrl,
                    updatedUser:activityDto.updatedUser,
                    versionFlag:activityDto.versionFlag
                });
                const updatedData = await this.activityRepository.findOne({
                    where:{Id:activityDto.Id}
                });
                if(updateActivity) {
                    const updateRoleActivity = await this.roleActivityService.updateAssignedRoleActivity(activityDto.Id, activityDto.roleIds)
                    response.status = true;
                    response.data   = updatedData;
                    response.statusCode = 50000;
                    response.internalMessage = 'Activity updated successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Activity update failed')
                }
            } else {
                const updateActivity = await this.activityRepository.update(activityDto.Id, {
                    activityName:activityDto.activityName,
                    activityUrl:activityDto.activityUrl,
                    updatedUser:activityDto.updatedUser,
                    versionFlag:activityDto.versionFlag
                });
                const updatedData = await this.activityRepository.findOne({
                    where:{Id:activityDto.Id}
                });
                if(updateActivity) {
                    const updateRoleActivity = await this.roleActivityService.updateAssignedRoleActivity(activityDto.Id, activityDto.roleIds)
                    response.status = true;
                    response.data   = updatedData;
                    response.statusCode = 50000;
                    response.internalMessage = 'Activity updated successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Activity update failed')
                }
            }
            return response;
        } catch (error) {
            throw error;
        }
    }

    async updateActivityStatus(queryParams, activityStatusDto:ActivityStatusDTO, request:Request): Promise<ActivityResponseModel> {
        const response = new ActivityResponseModel();
        try {
            if(queryParams.apiVersion == 1) {
                const updateStatus = await this.activityRepository.update(activityStatusDto.Id, activityStatusDto);
                const updatedData = await this.activityRepository.findOne({
                    where:{Id:activityStatusDto.Id}
                });
                if(updateStatus) {
                    response.status = true;
                    response.data   = updatedData;
                    response.statusCode = 50000;
                    response.internalMessage = 'Activity status updated successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Activity status update failed')
                }
            } else {
                const updateStatus = await this.activityRepository.update(activityStatusDto.Id, activityStatusDto);
                const updatedData = await this.activityRepository.findOne({
                    where:{Id:activityStatusDto.Id}
                });
                if(updateStatus) {
                    response.status = true;
                    response.data   = updatedData;
                    response.statusCode = 50000;
                    response.internalMessage = 'Activity status updated successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Activity status update failed')
                }
            }
            return response;
        } catch (error) {
            throw error;
        }
    }
}



